import {Folio, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import {useSnackbar} from "notistack";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {DigitalSigningService} from "../../../../digitalSigning/digitalSigning.service";
import {FolioSession} from "./useFolioSession";

export type FolioActionKind = 'finalize' | 'markAsPaid' | 'markAsUncollectable' | 'void' | 'capture' | 'release' | 'refund';

type Notify = (message: string, variant: 'success' | 'warning' | 'error') => void;

// What each action does once confirmed; every one ends with the folio as the backend has it now
const PERFORM: Record<FolioActionKind, (session: FolioSession, id: string, notify: Notify, t: (key: string) => string, amountCents?: number) => Promise<Folio | undefined>> = {
    finalize: async ({draft, guestLinkOpen, digitalSigning, refreshDigitalSigning}, id, notify, t) => {
        // Finalize charges what is saved; a failed save shows in the editor
        if (!(await draft.persist())) return undefined;
        // A finalized folio can't be confirmed by the guest any more, so the waiting link is voided -
        // unless the guest signed since the last poll: then the tip is confirmed, not dropped
        if (guestLinkOpen && digitalSigning.id) {
            const current = await DigitalSigningService.void(digitalSigning.id, {keepSigned: true});
            if (current.status === 'SIGNED' || current.status === 'CONFIRMED') {
                await refreshDigitalSigning();
                // Confirmed meanwhile, e.g. from another screen: that already finalized the folio
                if (current.status === 'CONFIRMED') return FolioService.getById(id);
                notify(t('The guest signed — use Confirm and charge'), 'warning');
                return undefined;
            }
        }
        await FolioService.finalize({id});
        return FolioService.getById(id);
    },
    markAsPaid: async (_session, id) => {
        await FolioService.markAsPaid({id});
        return FolioService.getById(id);
    },
    markAsUncollectable: async (_session, id) => {
        await FolioService.markAsUncollectable({id});
        return FolioService.getById(id);
    },
    void: async (_session, id) => {
        await FolioService.void({id});
        return FolioService.getById(id);
    },
    capture: async (_session, id, notify, t) => {
        const folio = await FolioService.capturePreauthorized({id});
        const captured = folio.status === FolioStatus.PAID;
        notify(captured ? t('Payment captured successfully') : t('Payment not captured'), captured ? 'success' : 'warning');
        return folio;
    },
    release: async (_session, id, notify, t) => {
        const folio = await FolioService.reverse({id});
        notify(folio.status === FolioStatus.RELEASED ? t('Funds released successfully') : t('Funds release completed'), 'success');
        return folio;
    },
    refund: async (_session, id, notify, t, amountCents) => {
        const folio = await FolioService.refund({id, amountCents});
        notify(folio.status === FolioStatus.REFUNDED ? t('Refund processed successfully') : t('Refund completed'), 'success');
        return folio;
    },
};

const FAILURE_KEYS: Partial<Record<FolioActionKind, string>> = {
    capture: 'Error capturing preauthorized payment',
    release: 'Error releasing funds',
    refund: 'Error processing refund',
};

/**
 * The folio's status changes, each behind its confirmation: request one, then confirm or cancel it.
 * A refund confirms with its amount.
 */
export const useFolioActions = (session: FolioSession) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [pending, setPending] = useState<FolioActionKind>();
    const [running, setRunning] = useState(false);
    const notify: Notify = (message, variant) => enqueueSnackbar(message, {variant});

    const confirm = async (amountCents?: number) => {
        const id = session.folio?.id;
        if (!pending || !id || running) return;
        setRunning(true);
        try {
            const updated = await PERFORM[pending](session, id, notify, key => t(key), amountCents);
            if (updated) {
                session.setFolio(updated);
                session.reportChange(updated);
            }
        } catch (error) {
            console.error(`Error running the folio action ${pending}:`, error);
            const failure = FAILURE_KEYS[pending];
            notify(failure ? t(failure) : t('Something went wrong'), 'error');
        }
        setRunning(false);
        setPending(undefined);
    };

    return {
        pending,
        running,
        request: (kind: FolioActionKind) => setPending(kind),
        cancel: () => !running && setPending(undefined),
        confirm,
    };
};

export type FolioActions = ReturnType<typeof useFolioActions>;
